import { type CartState } from "../types";
import * as PriceUtils from "../../../../../utils/PriceUtils";
import { createSelector } from "reselect";
import { type LineItem } from "../../models/CartData";
import * as WeightUtils from "../../../../../utils/WeightUtils";
import type { RootState } from "~/app/store";
import { getRuntimeConfig } from "~/common/config/runtimeConfig";
import { isConfiguratorProduct, isKITKITProduct } from "~/domains/product/types/ProductData";
import { getSurchargeValueFromTotalPrice } from "~/domains/product/utils/productDescriptorUtils";
import { evictStaleGroupsFromCache } from "~/features/cart/cartV2/utils/selectResolvedGroupedEntryV2";
import {
  getTotalPriceForLineItem,
  lineItemHasSurcharge,
  lineItemHasTariffSurcharge,
} from "~/features/cart/cart/utils/lineItemPriceUtils";

type PageWithCartState = { cart: CartState };

/**
 * Calculates the total item count across all line items in the cart.
 * Includes quantities from optional parts (for KIT products).
 */
export const selectTotalItemCount = createSelector(
  (state: PageWithCartState) => state.cart.packageEntriesById,
  (packageEntriesById) => {
    return Object.values(packageEntriesById).reduce((total, lineItem) => {
      return total + lineItem.quantity;
    }, 0);
  },
);

export const selectTotalCartWeight = createSelector(
  (state: PageWithCartState) => state.cart.packageEntriesById,
  (packageEntriesById) => {
    const weights = Object.values(packageEntriesById).map((entry: LineItem) =>
      WeightUtils.multiply(entry.weight, entry.quantity),
    );
    return WeightUtils.sum(...weights);
  },
);

export const selectTotalCartPrice = createSelector(
  (state: RootState) => state.cart.packageEntriesById,
  (state: RootState) => state.products.basic,
  (packageEntriesById, basic) => {
    const packEntries: LineItem[] = Object.values(packageEntriesById);
    const totalPerPack = packEntries
      .filter((lineItem) => !isKITKITProduct(basic[lineItem.productCode]))
      .map((packEntry) => {
        if (packEntry.netPrice) {
          const product = basic[packEntry.productCode];
          return getTotalPriceForLineItem(packEntry, product);
        }
        return null;
      });
    let totalPrice;
    if (totalPerPack.every((value) => !!value)) {
      // @ts-ignore
      totalPrice = PriceUtils.volatileSum(...totalPerPack);
    } else {
      totalPrice = null;
    }
    return totalPrice;
  },
);

export const selectTotalCartPriceWithoutDiscount = (campaignDiscountEnabled: boolean) =>
  createSelector(
    (state: RootState) => state.cart.packageEntriesById,
    (state: RootState) => state.products.basic,
    (packageEntriesById, basic): PriceData | null => {
      if (!campaignDiscountEnabled) {
        return null;
      }
      const packEntries: LineItem[] = Object.values(packageEntriesById);
      const totalPerPack = packEntries
        .filter((lineItem) => !isKITKITProduct(basic[lineItem.productCode]))
        .map((packEntry) => {
          if (packEntry.netPrice) {
            if (packEntry.saleCampaignEnabled) {
              return {
                quantity: packEntry.quantity,
                actualNetPrice: packEntry.actualNetPrice,
                actualSalePrice: packEntry.actualSalePrice,
                currencyIso: packEntry.actualNetPrice?.currencyIso,
              };
            }
          }
          return null;
        })
        .filter((item) => item !== null) as {
        quantity: number;
        actualNetPrice: { value: number; currencyIso: string };
        actualSalePrice: { value: number };
        currencyIso: string;
      }[];

      if (totalPerPack.length === 0) {
        return null;
      }

      const totalDiscount = totalPerPack.reduce((total, product) => {
        if (product.actualNetPrice && product.actualSalePrice) {
          const discountPerUnit = product.actualNetPrice.value - product.actualSalePrice.value;
          return total + discountPerUnit * product.quantity;
        }
        return total;
      }, 0);

      // Assuming all products have the same currency, take the first one
      const currencyIso = totalPerPack[0].currencyIso;

      return {
        value: totalDiscount,
        currencyIso,
      };
    },
  );

export const selectIsCartAboveThreshold = createSelector(
  (state: RootState) => state.cart.packageEntries,
  (packageEntries) => packageEntries.length > getRuntimeConfig().cartPnaThreshold,
);

/**
 * Cart page loading flag. Mirrors the local selector historically defined inline in
 * {@code CartPage.tsx}; exported here so shell/page-template code can consume the
 * named feature selector instead of inlining {@code RootState} shape knowledge.
 */
export const selectIsCartLoading = (state: RootState) => state.cartPage.loading;

/**
 * True when the cart has at least one package entry, independent of the cart-page
 * loading spinner. This is the React equivalent of the JSP cart-page template
 * conditional {@code not empty cartData.rootGroups}: backend cart entry groups
 * ({@code rootGroups}) are non-empty iff the cart has entries, and the React cart
 * slice's {@code packageEntries} is non-empty iff the cart has entries, so
 * {@code state.cart.packageEntries.length > 0} is equivalent to
 * {@code not empty cartData.rootGroups} for template slot placement.
 *
 * Use {@link selectIsCartEmpty} (CartPage.tsx) for body-internal empty/loading
 * branching; use this selector plus {@link selectIsCartLoading} for template-level
 * conditional slot regions so the slots do not flash empty-cart content while the
 * body spinner is showing.
 */
export const selectCartHasEntries = createSelector(
  (state: RootState) => state.cart.packageEntries,
  (packageEntries) => packageEntries.length > 0,
);

export const selectShowOptimePopupOnCheckout = createSelector(
  (state: RootState) => state.cart.packageEntriesById,
  (state: RootState) => state.products.basic,
  (packageEntriesById, allProducts) =>
    Object.values(packageEntriesById).some((entry) => allProducts[entry.productCode]?.isOptime ?? false),
);

export const selectPackageEntry = (packEntryId: number) => (state: RootState) =>
  state.cart.packageEntriesById[packEntryId];
export const selectPackageEntryMap = (state: RootState) => state.cart.packageEntriesById;
export const selectLineItem = (packEntryId: number) => (state: RootState) => state.cart.packageEntriesById[packEntryId];

export const selectProductCodeFromPackage = (packEntryId: number) => (state: RootState) => {
  return selectPackageEntry(packEntryId)(state).productCode;
};

/**
 * Returns the package entries indexed by group number.
 * Each line item appears under every groupNumber it belongs to.
 *
 * Side-effect: evicts stale entries from the per-group memoization cache in
 * `selectResolvedGroupedEntryV2` — only groups that no longer exist in the cart
 * are removed. Unchanged groups keep their cached results for stable references.
 */
export const selectLineItemsByGroupNumber = createSelector(
  (state: RootState) => state.cart.packageEntriesById,
  (packageEntriesById) => {
    const entriesByGroup: Record<number, LineItem[]> = {};
    Object.values(packageEntriesById).forEach((entry) => {
      entry.groupNumbers.forEach((groupNumber) => {
        if (!entriesByGroup[groupNumber]) {
          entriesByGroup[groupNumber] = [];
        }
        entriesByGroup[groupNumber].push(entry);
      });
    });
    evictStaleGroupsFromCache(new Set(Object.keys(entriesByGroup).map(Number)));
    return entriesByGroup;
  },
);

/**
 * Returns the package entries grouped by product code.
 * Package entries will be ordered by their entryNumber.
 */
export const selectLineItemsByProduct = createSelector(
  (state: RootState) => state.cart.packageEntriesById,
  (packageEntriesById) => {
    const entriesByProduct: Record<string, LineItem[]> = {};
    Object.values(packageEntriesById).forEach((packEntry) => {
      const productCode = packEntry.productCode;
      if (!entriesByProduct[productCode]) {
        entriesByProduct[productCode] = [];
      }
      entriesByProduct[productCode].push(packEntry);
    });
    Object.values(entriesByProduct).forEach((entries) => {
      entries.sort((a, b) => (a.entryNumber ?? -1) - (b.entryNumber ?? -1));
    });
    return entriesByProduct;
  },
);

export const selectLinearProductAmount = createSelector(
  (state: RootState) => state.products.basic,
  selectLineItemsByProduct,
  (products, lineItemsByProduct) => {
    return Object.keys(lineItemsByProduct).filter((productCode) => isConfiguratorProduct(products[productCode])).length;
  },
);
export const selectCartProductAmount = createSelector(
  selectLineItemsByProduct,
  (lineItemsByProduct) => Object.values(lineItemsByProduct).length,
);
export const selectCartContainsSurcharge = createSelector(
  (state: RootState) => state.cart.packageEntriesById,
  (packageEntriesById) => (Object.values(packageEntriesById) as LineItem[]).some(lineItemHasSurcharge),
);

export const selectCartContainsTariffSurcharge = createSelector(
  (state: RootState) => state.cart.packageEntriesById,
  (packageEntriesById) => (Object.values(packageEntriesById) as LineItem[]).some(lineItemHasTariffSurcharge),
);

export const selectCartSurcharge = createSelector(
  (state: RootState) => state.cart.packageEntriesById,
  (state: RootState) => state.products.basic,
  (packageEntriesById, basic) => {
    return PriceUtils.volatileSum(
      ...(Object.values(packageEntriesById) as LineItem[]).filter(lineItemHasSurcharge).map((lineItem) => {
        const isSubscription = !(basic[lineItem.productCode]?.quantityVisible ?? true);
        const totalPrice = getTotalPriceForLineItem(lineItem, isSubscription);
        return getSurchargeValueFromTotalPrice(totalPrice);
      }),
    );
  },
);
